import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Skeleton,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";

const MotionCard = motion(Card);

const glassMixin = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: "rgba(255,255,255,0.65)",
  border: "1px solid rgba(255,255,255,0.85)",
  boxShadow:
    "0 8px 32px rgba(99,102,241,0.08), 0 1px 0 rgba(255,255,255,0.9) inset",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
          border: "1px solid rgba(99,102,241,0.3)",
          borderRadius: "14px",
          px: 2,
          py: 1.5,
          boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
        }}
      >
        <Typography
          sx={{
            color: "#a5b4fc",
            fontSize: "0.68rem",
            fontWeight: 700,
            mb: 0.4,
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontSize: "1.3rem",
            fontWeight: 800,
            fontFamily: "'Syne', sans-serif",
            lineHeight: 1,
          }}
        >
          {payload[0].value?.toFixed(1)}
          <Typography
            component="span"
            sx={{
              color: "#818cf8",
              fontSize: "0.8rem",
              ml: 0.5,
              fontWeight: 600,
            }}
          >
            avg
          </Typography>
        </Typography>
      </Box>
    );
  }
  return null;
};

const TrendChart = ({ data }) => {
  if (!data || !Array.isArray(data)) {
    return (
      <Card sx={{ mb: 3, borderRadius: "24px", ...glassMixin }}>
        <CardContent sx={{ p: "24px !important" }}>
          <Skeleton width={200} height={24} sx={{ mb: 1 }} />
          <Skeleton width={300} height={16} sx={{ mb: 3 }} />
          <Skeleton
            variant="rounded"
            width="100%"
            height={240}
            sx={{ borderRadius: "16px" }}
          />
        </CardContent>
      </Card>
    );
  }

  const firstVal = data[0]?.avg ?? 0;
  const lastVal = data[data.length - 1]?.avg ?? 0;
  const isPositive = lastVal >= firstVal;
  const changePct =
    firstVal > 0 ? (((lastVal - firstVal) / firstVal) * 100).toFixed(1) : 0;
  const avgLine =
    data.reduce((sum, d) => sum + (d.avg || 0), 0) / (data.length || 1);

  return (
    <MotionCard
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      sx={{
        mb: 3,
        borderRadius: "24px",
        overflow: "hidden",
        position: "relative",
        ...glassMixin,
      }}
    >
      {/* decorative gradient bar */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background:
            "linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, #10b981 100%)",
        }}
      />

      {/* background glow inside card */}
      <Box
        sx={{
          position: "absolute",
          bottom: -40,
          right: -40,
          width: 260,
          height: 260,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <CardContent sx={{ p: "24px !important", position: "relative" }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          mb={3}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(139,92,246,0.15) 100%)",
                  border: "1px solid rgba(99,102,241,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShowChartRoundedIcon sx={{ color: "#6366f1", fontSize: 20 }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 700,
                  color: "#111827",
                  fontSize: "1rem",
                }}
              >
                Performance Trend
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{ color: "#9ca3af", ml: "54px", fontSize: "0.78rem" }}
            >
              Average score over time
            </Typography>
          </Box>

          <Chip
            icon={
              isPositive ? (
                <TrendingUpRoundedIcon style={{ fontSize: 16 }} />
              ) : (
                <TrendingDownRoundedIcon style={{ fontSize: 16 }} />
              )
            }
            label={`${isPositive ? "+" : ""}${changePct}%`}
            size="small"
            sx={{
              background: isPositive
                ? "rgba(16,185,129,0.1)"
                : "rgba(239,68,68,0.08)",
              color: isPositive ? "#10b981" : "#ef4444",
              fontWeight: 700,
              fontSize: "0.8rem",
              border: `1px solid ${
                isPositive ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.2)"
              }`,
              borderRadius: "10px",
              "& .MuiChip-icon": { color: "inherit", fontSize: 16 },
            }}
          />
        </Stack>

        <ResponsiveContainer width="100%" height={240}>
          <AreaChart
            data={data}
            margin={{ top: 8, right: 16, left: -16, bottom: 0 }}
          >
            <defs>
              <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6366f1" stopOpacity={0.25} />
                <stop offset="60%" stopColor="#8b5cf6" stopOpacity={0.08} />
                <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(0,0,0,0.05)"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fontWeight: 600, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fontWeight: 600, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              domain={["auto", "auto"]}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={avgLine}
              stroke="rgba(99,102,241,0.2)"
              strokeDasharray="5 5"
              label={{
                value: `Avg: ${avgLine.toFixed(1)}`,
                position: "right",
                fontSize: 10,
                fill: "#9ca3af",
                fontWeight: 600,
              }}
            />
            <Area
              type="monotone"
              dataKey="avg"
              stroke="url(#strokeGrad)"
              strokeWidth={2.5}
              fill="url(#trendGradient)"
              dot={{ fill: "#6366f1", r: 3.5, strokeWidth: 0 }}
              activeDot={{
                r: 7,
                fill: "#6366f1",
                stroke: "#fff",
                strokeWidth: 2.5,
                filter: "url(#glow)",
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </MotionCard>
  );
};

export default TrendChart;
